import React, { useEffect, useState } from "react";
import { FieldConfig, FieldSectionConfig } from "../../../../../models/Models";
import { Content, ContentGroup, PreviewVideoAsset } from "../../../../../models/vod/Models";
import {
    useEditContentGroupPreviewVideoAssets,
    useEditContentPreviewVideoAssets,
} from "../../../../../services/vod/Query";
import Modal from "../../../../../context/modal/Modal";
import EditForm from "./EditForm";
import dayjs from "dayjs";

interface Props {
    modalId: string;
    openModal: boolean;
    onClose: () => void;
    contentGroup?: ContentGroup;
    content?: Content;
}

const PreviewVideoAssetFormModal = ({
    contentGroup,
    content,
    modalId,
    openModal,
    onClose,
}: Props) => {
    const [ previewVideoAssets, setPreviewVideoAssets ] = useState<PreviewVideoAsset[]>(
        content?.previewVideoAssets ?? contentGroup?.previewVideoAssets ?? []
    );
    const editContentGroupPreviewVideoAssets = useEditContentGroupPreviewVideoAssets();
    const editContentPreviewVideoAssets = useEditContentPreviewVideoAssets();

    const formatPreviewVideoAssets = (previewVideoAssets?: PreviewVideoAsset[] | null) => {
        return !previewVideoAssets ? [] : [
            ...previewVideoAssets.map(previewVideoAsset => {
                const runTime = previewVideoAsset.runTime;
                return {
                    ...previewVideoAsset,
                    runTime: !runTime || !runTime.includes("PT") ? runTime : dayjs
                        .duration(runTime).format("HH:mm:ss"),
                };
            }),
        ];
    };

    useEffect(() => {
        if (contentGroup)
            setPreviewVideoAssets(
                formatPreviewVideoAssets(contentGroup.previewVideoAssets),
            );
        else if (content)
            setPreviewVideoAssets(
                formatPreviewVideoAssets(content.previewVideoAssets),
            );

    }, [ contentGroup, content ]);

    const previewVideoAssetConfig: FieldConfig<PreviewVideoAsset>[] = [
        {
            path: "previewVideoAssets",
            label: "Preview Video Asset",
            type: "array",
            isAppendable: false,
            areItemsRemovable: false,
            isArray: true,
            fields: [
                { path: "url", label: "Url", type: "text" },
                { path: "runTime", label: "Runtime", type: "duration" },
            ],
        },
    ];
    const fieldSectionConfigs: FieldSectionConfig<PreviewVideoAsset>[] = [ {
        fields: previewVideoAssetConfig,
    } ];

    const submit = async (submittedPreviewVideoAsset: { previewVideoAssets: PreviewVideoAsset[] }) => {
        const data = submittedPreviewVideoAsset.previewVideoAssets.map(data => {
            let runTime = data.runTime;

            if (runTime) {
                const splitDuration = runTime.split(":");

                if (splitDuration.length === 3) {
                    const [ hours, minutes, seconds ] = splitDuration.map(timeUnit => Number(timeUnit));
                    runTime = dayjs.duration({ hours, minutes, seconds }).toISOString();
                }
            }
            data.runTime = runTime;
            return data;
        });

        if (contentGroup)
            await editContentGroupPreviewVideoAssets.mutateAsync({
                ...contentGroup,
                previewVideoAssets: data,
            });
        else if (content){
            await editContentPreviewVideoAssets.mutateAsync({
                ...content,
                previewVideoAssets: data,
            });
        }
    };

    return (
        <Modal
            modalTitle={"Edit Preview Video Assets"}
            modalId={modalId}
            modalSize={"w-[60vw] h-fit max-h-[80vh]"}
            openModal={openModal} onClose={onClose}
        >
            <EditForm
                modalId={modalId}
                config = {fieldSectionConfigs}
                initialValues = {{ previewVideoAssets: previewVideoAssets }}
                onSubmit = {submit}
            />
        </Modal>
    );
};

export default PreviewVideoAssetFormModal;